/**
 * RestoID — the consent screen's organization selector.
 * ---------------------------------------------------------------------------
 * Replaces the whole `src/pages/Consent/OrganizationSelector` tree (the selector,
 * `OrganizationItem` and the `react-modal`-based `OrganizationSelectorModal`).
 *
 * Upstream behaviour kept, all of it:
 *   - renders nothing when the tenant sent no organizations, or before the page
 *     has picked a default one (`organizations.length === 0 || !selected`);
 *   - the title is still `description.authorize_organization_access`;
 *   - the SELECTED organization's own `missingResourceScopes` are listed above
 *     the picker, sorted so the reserved organization resource is always first,
 *     and that one group is named `description.organization_scopes` while every
 *     other keeps its resource name. The sort still runs on a `slice()` copy, so
 *     the response object is never mutated;
 *   - picking an organization closes the list and calls `onSelect`, which is what
 *     the page sends to `consent(organizationId)`.
 *
 * One deliberate difference: the dropdown is an inline disclosure under the
 * trigger instead of a `react-modal` portal positioned by a `useLayoutEffect`
 * that measures the trigger, re-measures on `resize` and clamps against
 * `window.innerHeight`. All of that existed to make a portal behave like an
 * inline list; the card is narrow and already scrolls, so the list can simply BE
 * inline — no portal, no measuring, no mobile/desktop split.
 *
 * What the portal DID give the user is kept, because it is behaviour and not
 * layout: `ReactModal`'s `onRequestClose` fired on Escape and on a press outside
 * the list, so the dismissal effect below listens for both on the document while
 * the list is open. A dismissal that only worked while a row happened to hold
 * focus would be a regression, not a simplification.
 */
import { ReservedResource } from '@logto/core-kit';
import { type ConsentInfoResponse } from '@logto/schemas';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ConsentScopeGroup, ConsentScopeGroups } from './ConsentScopes';
import { C, FONT, Icon } from './restoid';

export type Organization = Exclude<ConsentInfoResponse['organizations'], undefined>[number];

/** One line of truncated text — organization names are unbounded. */
const ellipsis = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

type TriggerProps = {
  name: string;
  isOpen: boolean;
  onToggle: () => void;
};

/** The closed selector — the current organization, with a disclosure chevron. */
const SelectorTrigger = ({ name, isOpen, onToggle }: TriggerProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type="button"
      aria-expanded={isOpen}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        padding: '13px 15px',
        borderRadius: 12,
        cursor: 'pointer',
        background: isHovered || isOpen ? C.cardHover : C.card,
        border: `1px solid ${isHovered || isOpen ? C.line : C.lineSoft}`,
        transition: 'background .15s ease, border-color .15s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onToggle}
    >
      <span style={{ flex: '0 0 auto', display: 'inline-flex', color: C.brand }}>
        <Icon name="building" size={19} />
      </span>
      <span
        style={{ flex: 1, minWidth: 0, color: C.text, font: `600 15px/1.3 ${FONT}`, ...ellipsis }}
      >
        {name}
      </span>
      <span
        style={{
          flex: '0 0 auto',
          display: 'inline-flex',
          color: C.faint,
          transform: isOpen ? 'rotate(90deg)' : 'none',
          transition: 'transform .15s ease',
        }}
      >
        <Icon name="chevR" size={18} />
      </span>
    </button>
  );
};

type OptionProps = {
  organization: Organization;
  isSelected: boolean;
  onSelect: () => void;
};

/** One row of the open list. */
const OrganizationOption = ({ organization, isSelected, onSelect }: OptionProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type="button"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        width: '100%',
        textAlign: 'left',
        padding: '10px 11px',
        borderRadius: 9,
        cursor: 'pointer',
        background: isHovered ? C.cardHover : 'transparent',
        border: 'none',
        transition: 'background .15s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onSelect}
    >
      <span
        style={{ flex: '0 0 auto', display: 'inline-flex', color: isSelected ? C.brand : C.faint }}
      >
        <Icon name={isSelected ? 'check' : 'building'} size={17} />
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: isSelected ? C.text : C.sub,
          font: `500 14px/1.3 ${FONT}`,
          ...ellipsis,
        }}
      >
        {organization.name}
      </span>
    </button>
  );
};

type Props = {
  organizations: Organization[];
  selectedOrganization: Organization | undefined;
  onSelect: (organization: Organization) => void;
};

export const ConsentOrganization = ({ organizations, selectedOrganization, onSelect }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);

  // What `ReactModal`'s `onRequestClose` did for the upstream dropdown: Escape
  // from anywhere, and a press outside the trigger + list. Bound to the document
  // only while the list is open, and torn down with it.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const close = () => {
      setIsOpen(false);
    };

    const handlePointerDown = ({ target }: PointerEvent) => {
      // A press on the trigger is the toggle's own business — leave it alone.
      if (target instanceof Node && selectorRef.current?.contains(target)) {
        return;
      }

      close();
    };

    const handleKeyDown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') {
        close();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (organizations.length === 0 || !selectedOrganization) {
    return null;
  }

  const { missingResourceScopes: resourceScopes } = selectedOrganization;

  const groups: ConsentScopeGroup[] = (resourceScopes ?? [])
    .slice()
    // Sort the scopes to make sure the organization scope is always on top.
    .sort(({ resource: resourceA }, { resource: resourceB }) => {
      if (resourceA.id === ReservedResource.Organization) {
        return -1;
      }
      return resourceB.id === ReservedResource.Organization ? 1 : 0;
    })
    .map(({ resource, scopes }) => ({
      key: resource.id,
      name:
        resource.id === ReservedResource.Organization
          ? t('description.organization_scopes')
          : resource.name,
      scopes,
    }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ margin: 0, color: C.sub, font: `400 14px/1.5 ${FONT}` }}>
        {t('description.authorize_organization_access')}
      </p>
      <ConsentScopeGroups groups={groups} />
      <div ref={selectorRef}>
        <SelectorTrigger
          name={selectedOrganization.name}
          isOpen={isOpen}
          onToggle={() => setIsOpen((previous) => !previous)}
        />
        {isOpen ? (
          <div
            style={{
              marginTop: 8,
              padding: 6,
              borderRadius: 12,
              background: C.card,
              border: `1px solid ${C.line}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {organizations.map((organization) => (
              <OrganizationOption
                key={organization.id}
                organization={organization}
                isSelected={organization.id === selectedOrganization.id}
                onSelect={() => {
                  setIsOpen(false);
                  onSelect(organization);
                }}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};
